import React, { useState, useEffect, useMemo } from "react";
import { createPortal } from "react-dom";
import type {
  KanbanBoardProps,
  KanbanColumnData,
  KanbanCardData,
} from "./types";
import { KanbanColumn } from "./KanbanColumn";
import { KanbanCard } from "./KanbanCard";
import {
  DndContext,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
  DragOverlay,
  type DragEndEvent,
  type DragStartEvent,
  type DragOverEvent,
  MeasuringStrategy,
  rectIntersection,
} from "@dnd-kit/core";
import { restrictToWindowEdges } from "@dnd-kit/modifiers";
import {
  SortableContext,
  arrayMove,
  verticalListSortingStrategy, // For cards within a column
} from "@dnd-kit/sortable";

// Helper to find a column by ID
const findColumn = (
  columns: KanbanColumnData[],
  id: string | number | undefined
) => (id ? columns.find((col) => col.id === id) : undefined);

const BoardInner: React.FC<
  Omit<KanbanBoardProps, "initialColumns"> & {
    columns: KanbanColumnData[];
    setColumns: React.Dispatch<React.SetStateAction<KanbanColumnData[]>>;
    externalContainerRefForOffset?: React.RefObject<HTMLDivElement | null>;
  }
> = ({ columns, setColumns, onCardMove, externalContainerRefForOffset, variant = 'default' }) => {
  const [activeId, setActiveId] = useState<string | number | null>(null);
  const [activeCardData, setActiveCardData] = useState<KanbanCardData | null>(
    null
  );
  const [activeCardOriginalColumnId, setActiveCardOriginalColumnId] = useState<
    string | null
  >(null);
  const [clonedColumns, setClonedColumns] = useState<KanbanColumnData[] | null>(
    null
  );
  const [dragStartPointerVsCardOffset, setDragStartPointerVsCardOffset] =
    useState({ x: 0, y: 0 });

  const sensors = useSensors(
    useSensor(PointerSensor, {
      // Require the mouse to move by 8 pixels before starting a drag
      // to avoid dragging on a simple click
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor)
  );

  const handleDragStart = (event: DragStartEvent) => {
    const { active, activatorEvent } = event; // Destructure activatorEvent
    setActiveId(active.id);
    const card = active.data.current?.card as KanbanCardData | undefined;
    const columnId = active.data.current?.columnId as string | undefined;
    if (card) setActiveCardData(card);
    if (columnId) setActiveCardOriginalColumnId(columnId);
    setClonedColumns(columns);

    // Calculate offset between pointer and card's top-left
    if (
      activatorEvent instanceof MouseEvent ||
      activatorEvent instanceof TouchEvent
    ) {
      const pointer =
        activatorEvent instanceof MouseEvent
          ? { x: activatorEvent.clientX, y: activatorEvent.clientY }
          : {
              x: activatorEvent.touches[0].clientX,
              y: activatorEvent.touches[0].clientY,
            };

      const cardStartRect = active.rect.current.translated;
      if (cardStartRect) {
        setDragStartPointerVsCardOffset({
          x: pointer.x - cardStartRect.left,
          y: pointer.y - cardStartRect.top,
        });
      } else {
        setDragStartPointerVsCardOffset({ x: 0, y: 0 });
      }
    } else {
      setDragStartPointerVsCardOffset({ x: 0, y: 0 });
    }
  };

  const handleDragOver = (event: DragOverEvent) => {
    const { active, over } = event;
    if (!over || !activeCardData) return;

    const activeItemId = active.id.toString();
    const overId = over.id.toString();

    // Get current column of active item (it might have moved in a previous dragOver)
    let currentColumnOfActiveItem: KanbanColumnData | undefined;
    for (const col of columns) {
      if (col.cards.some((c) => c.id === activeItemId)) {
        currentColumnOfActiveItem = col;
        break;
      }
    }
    if (!currentColumnOfActiveItem) return; // Active item not found in any column (should not happen if state is consistent)

    const activeContainerId = currentColumnOfActiveItem.id;

    let overContainerId: string | undefined;
    if (over.data.current?.type === "column") {
      overContainerId = overId;
    } else if (over.data.current?.type === "card") {
      overContainerId = over.data.current?.columnId as string | undefined;
    }

    if (!overContainerId || activeContainerId === overContainerId) {
      // Not moving to a different container, or invalid over container
      return;
    }

    setColumns((prevColumns) => {
      const sourceColIndex = prevColumns.findIndex(
        (col) => col.id === activeContainerId
      );
      const destColIndex = prevColumns.findIndex(
        (col) => col.id === overContainerId
      );

      if (sourceColIndex === -1 || destColIndex === -1) {
        return prevColumns; // Should not happen
      }

      // Deep clone to avoid direct state mutation issues with array splicing
      const newColumns = JSON.parse(
        JSON.stringify(prevColumns)
      ) as KanbanColumnData[];

      const sourceCards = newColumns[sourceColIndex].cards;
      const destCards = newColumns[destColIndex].cards;

      const activeIndexInSource = sourceCards.findIndex(
        (card) => card.id === activeItemId
      );
      if (activeIndexInSource === -1) {
        return prevColumns; // Card not found in its supposed source column
      }

      const [movedItem] = sourceCards.splice(activeIndexInSource, 1);

      // Determine insertion index in destination
      let insertionIndexInDest = destCards.length; // Default to end
      if (over.data.current?.type === "card") {
        const overCardIndex = destCards.findIndex((card) => card.id === overId);
        if (overCardIndex !== -1) {
          insertionIndexInDest = overCardIndex;
        }
      }

      destCards.splice(insertionIndexInDest, 0, movedItem);
      return newColumns;
    });
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (!activeId || !activeCardData || !activeCardOriginalColumnId) {
      // Reset state if drag ended prematurely or with incomplete data
      setActiveId(null);
      setActiveCardData(null);
      setActiveCardOriginalColumnId(null);
      setClonedColumns(null);
      return;
    }

    const activeItemId = active.id.toString();
    let finalColumnId = activeCardOriginalColumnId; // Assume it didn't move columns initially
    let finalIndex = -1;

    // Find the current column and index of the dragged item from the potentially modified `columns` state
    for (const col of columns) {
      const cardIndex = col.cards.findIndex((c) => c.id === activeItemId);
      if (cardIndex !== -1) {
        finalColumnId = col.id;
        finalIndex = cardIndex;
        break;
      }
    }

    if (over && finalColumnId && finalIndex !== -1) {
      const overId = over.id.toString();
      const sourceColIdForCallback = activeCardOriginalColumnId; // The column where drag started
      const targetColIdForCallback = finalColumnId;
      let targetIndexForCallback = finalIndex;

      // If it's the same column, we might need to re-calculate index based on `over` for arrayMove
      if (targetColIdForCallback === sourceColIdForCallback) {
        const currentColumn = findColumn(columns, targetColIdForCallback);
        if (currentColumn) {
          const oldIdx = currentColumn.cards.findIndex(
            (c) => c.id === activeItemId
          ); // Should be == finalIndex
          // If over.id is a card, use its index. If over.id is a column, finalIndex (from list end) is fine.
          const newIdx =
            over.data.current?.type === "card"
              ? currentColumn.cards.findIndex((c) => c.id === overId)
              : -1;

          if (oldIdx !== -1 && newIdx !== -1 && oldIdx !== newIdx) {
            setColumns((prev) => {
              const newCols = JSON.parse(
                JSON.stringify(prev)
              ) as KanbanColumnData[];
              const colToUpdateIndex = newCols.findIndex(
                (c) => c.id === targetColIdForCallback
              );
              if (colToUpdateIndex !== -1) {
                const reorderedCards = arrayMove(
                  newCols[colToUpdateIndex].cards,
                  oldIdx,
                  newIdx
                );
                newCols[colToUpdateIndex].cards = reorderedCards;
                targetIndexForCallback = newIdx; // Update index for callback
                if (onCardMove) {
                  onCardMove(
                    activeItemId,
                    sourceColIdForCallback,
                    targetColIdForCallback,
                    targetIndexForCallback
                  );
                }
                return newCols;
              }
              return prev;
            });
          } else if (onCardMove) {
            // No reorder needed, but call onCardMove if different from original
            onCardMove(
              activeItemId,
              sourceColIdForCallback,
              targetColIdForCallback,
              targetIndexForCallback
            );
          }
        }
      } else if (onCardMove) {
        // Different column, state already updated by handleDragOver
        // Now, ensure the card data within the columns state reflects the new columnId
        setColumns((prev) => {
          const newCols = JSON.parse(
            JSON.stringify(prev)
          ) as KanbanColumnData[];
          let cardFound = false;
          for (const col of newCols) {
            const cardIndex = col.cards.findIndex((c) => c.id === activeItemId);
            if (cardIndex !== -1) {
              // If this is the destination column, update the card's columnId
              if (col.id === targetColIdForCallback) {
                col.cards[cardIndex].columnId = targetColIdForCallback;
              }
              cardFound = true;
              // No break here, ensure card only has its columnId updated in its *final* destination column
              // (Though with current dragOver logic, it should only exist in one place)
            }
          }
          return newCols;
        });

        onCardMove(
          activeItemId,
          sourceColIdForCallback,
          targetColIdForCallback,
          targetIndexForCallback
        );
      }

      // Display an alert with card details after a successful drop
      if (activeCardData) {
        /* showInfo(
          `Card "${activeCardData.title}" Moved`,
          `ID: ${activeCardData.id}\nContent: ${
            activeCardData.content || "N/A"
          }\nColumn: ${finalColumnId} | Index: ${targetIndexForCallback}`
        ); */
      }
    }
    // If !over, dnd-kit's SortableContext usually handles reverting to original place if items are managed correctly.
    // If a card was moved in handleDragOver and then dropped outside, we might need to explicitly revert.
    // For now, we rely on dnd-kit's revert or the user's state management if initialColumns is re-fed.
    // The current logic assumes that if 'over' is null, no action/callback is triggered, and previous state (potentially modified by dragOver) persists or is handled by dnd-kit's cancel.

    setActiveId(null);
    setActiveCardData(null);
    setActiveCardOriginalColumnId(null);
    setClonedColumns(null);
  };

  const handleDragCancel = () => {
    if (clonedColumns) {
      setColumns(clonedColumns);
    }
    setActiveId(null);
    setActiveCardData(null);
    setActiveCardOriginalColumnId(null);
    setClonedColumns(null);
  };

  // Memoize card IDs for SortableContext
  const columnCardIds = useMemo(
    () =>
      columns.reduce((acc, col) => {
        acc[col.id] = col.cards.map((card) => card.id);
        return acc;
      }, {} as Record<string, string[]>),
    [columns]
  );

  // Modifier to align the DragOverlay based on the initial pointer vs card offset
  const alignOverlayWithPointerOffsetModifier = ({
    transform,
  }: {
    transform: { x: number; y: number; scaleX?: number; scaleY?: number };
  }) => {
    return {
      x: transform.x - dragStartPointerVsCardOffset.x,
      y: transform.y - dragStartPointerVsCardOffset.y,
      scaleX: transform.scaleX || 1,
      scaleY: transform.scaleY || 1,
    };
  };

  return (
    <>
      <DndContext
        sensors={sensors}
        collisionDetection={rectIntersection}
        onDragStart={handleDragStart}
        onDragOver={handleDragOver}
        onDragEnd={handleDragEnd}
        onDragCancel={handleDragCancel}
        measuring={{
          droppable: { strategy: MeasuringStrategy.BeforeDragging },
        }}
      >
        <div className="flex flex-row space-x-4 overflow-x-auto min-h-[400px] items-start" style={{ minWidth: 'fit-content' }}>
          {columns.map((column) => (
            <SortableContext
              key={column.id}
              items={columnCardIds[column.id] || []}
              strategy={verticalListSortingStrategy}
            >
              <KanbanColumn
                column={column}
                isDraggingActive={!!activeId}
                activeCardId={activeId}
                variant={variant}
              />
            </SortableContext>
          ))}
        </div>
        {createPortal(
          <DragOverlay
            dropAnimation={null}
            modifiers={[
              restrictToWindowEdges,
              // alignOverlayWithPointerOffsetModifier, // Temporarily removed for testing
            ]}
          >
            {activeId && activeCardData && activeCardOriginalColumnId ? (
              <KanbanCard
                card={activeCardData}
                columnId={activeCardOriginalColumnId}
                isOverlay={true}
                variant={variant}
              />
            ) : null}
          </DragOverlay>,
          document.body
        )}
      </DndContext>
    </>
  );
};

export const KanbanBoard: React.FC<KanbanBoardProps> = ({
  initialColumns,
  onCardMove,
  variant = 'default',
}) => {
  const [columns, setColumns] = useState<KanbanColumnData[]>(initialColumns);

  // Sync with initialColumns prop. Consider deep comparison if prop might change frequently without intent to reset.
  useEffect(() => {
    setColumns(initialColumns);
  }, [initialColumns]);

  return (
    <BoardInner
      columns={columns}
      setColumns={setColumns}
      onCardMove={onCardMove}
      variant={variant}
    />
  );
};
